import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  Button,
  IconButton,
  TextField,
  Switch,
  Popover,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import './CreateOneOnOneSessonModel.css';
import { axiosInstance } from '../../../../utils/tokenUtils';
import ScheduleCreationSuccessModel from '../ScheduleCreationSuccessModel/ScheduleCreationSuccessModel';
// Import time picker components
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import ReactDOM from 'react-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { toast } from 'react-hot-toast';

// Setup dayjs plugins for timezone handling
dayjs.extend(utc);
dayjs.extend(timezone);

// Custom renderer that hides the minute hand in the hours view
const customTimeViewRenderer = props => {
  const { view, value, onChange, ampm, ...other } = props;

  // For hours view, render the standard clock
  if (view === 'hours') {
    return renderTimeViewClock({
      view,
      value,
      onChange,
      ampm,
      ...other,
    });
  }

  // For minutes view, which is shown after hours are selected
  return renderTimeViewClock({
    view,
    value,
    onChange,
    ampm,
    ...other,
  });
};

const CreateOneOnOneSessionModel = ({ open, onClose, onContinue }) => {
  const [days, setDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });

  const [timeSlots, setTimeSlots] = useState({
    monday: [{ from: '', to: '' }],
    tuesday: [{ from: '', to: '' }],
    wednesday: [{ from: '', to: '' }],
    thursday: [{ from: '', to: '' }],
    friday: [{ from: '', to: '' }],
    saturday: [{ from: '', to: '' }],
    sunday: [{ from: '', to: '' }],
  });

  const [description, setDescription] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [copyAnchorEl, setCopyAnchorEl] = useState(null);
  const [copySourceDay, setCopySourceDay] = useState('');
  const [copySourceIndex, setCopySourceIndex] = useState(null);
  const [copyTargetDays, setCopyTargetDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });
  const [isLoading, setIsLoading] = useState(false);

  // State for currently active time picker to track which one is open
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const [clockView, setClockView] = useState('hours'); // 'hours' or 'minutes'

  // Add ref for time picker positioning
  const timePickerRefs = useRef({});

  // Helper function to convert local time to UTC
  const convertLocalToUTC = timeString => {
    if (!timeString) return null;
    // Assuming timeString is in 'HH:mm' format or '12-hour format with AM/PM'
    let localDate;
    if (timeString.includes('AM') || timeString.includes('PM')) {
      localDate = dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
    } else {
      const [hours, minutes] = timeString.split(':');
      localDate = dayjs().hour(hours).minute(minutes);
    }
    return localDate.utc().format('HH:mm');
  };

  const handleDayToggle = day => {
    setDays({
      ...days,
      [day]: !days[day],
    });

    // Clear error when toggling days
    setErrorMessage('');
  };

  // Modified handleTimeChange to remove validation for "to time must be after from time"
  const handleTimeChange = (day, index, field, value) => {
    // If value is null, the field is being cleared
    if (!value) {
      const newTimeSlots = { ...timeSlots };
      newTimeSlots[day][index][field] = '';
      setTimeSlots(newTimeSlots);
      setClockView('hours'); // Reset to hours view
      return;
    }

    // For hours selection, update the hours and keep existing minutes or use 00
    if (clockView === 'hours') {
      const newTimeSlots = { ...timeSlots };
      const currentTimeValue = newTimeSlots[day][index][field];

      if (currentTimeValue) {
        // If there's an existing time value, extract its minutes
        const existingTime = stringToTime(currentTimeValue);
        if (existingTime) {
          // Keep the existing minutes but update the hours
          const updatedTime = value.minute(existingTime.minute());
          newTimeSlots[day][index][field] = updatedTime.format('hh:mm A');
        } else {
          // If parsing failed, use 00 minutes with the new hour
          newTimeSlots[day][index][field] = value.minute(0).format('hh:mm A');
        }
      } else {
        // If no existing time, use 00 minutes with the new hour
        newTimeSlots[day][index][field] = value.minute(0).format('hh:mm A');
      }

      setTimeSlots(newTimeSlots);
      // Switch to minutes view to let user select minutes if desired
      setClockView('minutes');
      return;
    }

    // For minutes selection, format and update the complete time
    const newTimeSlots = { ...timeSlots };
    const formattedTime = value.format('hh:mm A');
    newTimeSlots[day][index][field] = formattedTime;

    // We're no longer validating that "to time" must be after "from time"
    // Any case where to < from will be treated as an overnight session

    setTimeSlots(newTimeSlots);

    // Reset to hours view for next selection
    setClockView('hours');
  };

  // Function to handle opening a time picker
  const handleTimePickerOpen = (day, index, field) => {
    // Reset the current field's value when opening the picker to allow re-selection
    if (timeSlots[day][index][field]) {
      const newTimeSlots = { ...timeSlots };
      // Don't actually clear the value in the UI, just in the internal state when opening
      // This allows the picker to reset while keeping the display value
      setActiveTimePicker({
        day,
        index,
        field,
        previousValue: newTimeSlots[day][index][field], // Store previous value
      });
    } else {
      setActiveTimePicker({ day, index, field });
    }
    setClockView('hours'); // Always start with hours view
  };

  // Function to handle closing a time picker
  const handleTimePickerClose = () => {
    // If user cancels without selecting a new value, restore the previous value
    if (activeTimePicker?.previousValue) {
      const { day, index, field, previousValue } = activeTimePicker;
      const newTimeSlots = { ...timeSlots };

      // Only restore if the value was actually cleared (user didn't select a new time)
      if (!newTimeSlots[day][index][field]) {
        newTimeSlots[day][index][field] = previousValue;
        setTimeSlots(newTimeSlots);
      }
    }

    setActiveTimePicker(null);
    setClockView('hours'); // Reset to hours view
  };

  // Utility function to convert string time to dayjs object
  const stringToTime = timeString => {
    if (!timeString) return null;
    // Parse time including AM/PM format
    return dayjs(`2023-01-01 ${timeString}`, 'YYYY-MM-DD hh:mm A');
  };

  const addTimeSlot = day => {
    const newTimeSlots = { ...timeSlots };
    newTimeSlots[day].push({ from: '', to: '' });
    setTimeSlots(newTimeSlots);
  };

  const removeTimeSlot = (day, index) => {
    const newTimeSlots = { ...timeSlots };
    newTimeSlots[day].splice(index, 1);
    if (newTimeSlots[day].length === 0) {
      newTimeSlots[day].push({ from: '', to: '' });
    }
    setTimeSlots(newTimeSlots);
  };

  const openCopyPopover = (event, day, index) => {
    setCopyAnchorEl(event.currentTarget);
    setCopySourceDay(day);
    setCopySourceIndex(index);

    // Reset target days except the source day
    const newCopyTargetDays = {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    };
    setCopyTargetDays(newCopyTargetDays);

    // Clear any previous errors related to copying
    setErrorMessage('');
  };

  const closeCopyPopover = () => {
    setCopyAnchorEl(null);
    setCopySourceDay('');
    setCopySourceIndex(null);
  };

  const handleCopyTargetChange = day => {
    setCopyTargetDays({
      ...copyTargetDays,
      [day]: !copyTargetDays[day],
    });
  };

  const applyCopy = () => {
    const sourceSlot = timeSlots[copySourceDay][copySourceIndex];

    // Prevent copying if source slot is incomplete
    if (!sourceSlot?.from || !sourceSlot?.to) {
      setErrorMessage(
        'Please select both "From" and "To" times for the source slot before copying.'
      );
      return;
    }

    // Determine selected target days excluding the source day
    const selectedTargets = Object.keys(copyTargetDays).filter(
      day => day !== copySourceDay && copyTargetDays[day]
    );

    if (selectedTargets.length === 0) {
      setErrorMessage('Please select at least one day to copy to.');
      return;
    }

    const newTimeSlots = { ...timeSlots };
    const newDays = { ...days };

    selectedTargets.forEach(day => {
      // Enable the day if it's not already enabled
      newDays[day] = true;

      // Ensure the day has a time slots array
      if (!Array.isArray(newTimeSlots[day])) {
        newTimeSlots[day] = [];
      }

      // If there are no slots yet, create the first one
      if (newTimeSlots[day].length === 0) {
        newTimeSlots[day] = [{ from: sourceSlot.from, to: sourceSlot.to }];
      } else {
        // If there is a single empty default slot, fill it instead of adding a new one
        const hasOnlyEmptyDefault =
          newTimeSlots[day].length === 1 &&
          !newTimeSlots[day][0]?.from &&
          !newTimeSlots[day][0]?.to;

        if (hasOnlyEmptyDefault) {
          newTimeSlots[day][0] = { from: sourceSlot.from, to: sourceSlot.to };
        } else {
          // Avoid adding duplicate slots
          const isDuplicate = newTimeSlots[day].some(
            s => s.from === sourceSlot.from && s.to === sourceSlot.to
          );

          if (!isDuplicate) {
            newTimeSlots[day].push({ from: sourceSlot.from, to: sourceSlot.to });
          }
        }
      }
    });

    setDays(newDays);
    setTimeSlots(newTimeSlots);

    // Clear errors and close popover after successful copy
    setErrorMessage('');
    closeCopyPopover();
  };

  // Remove validation for time slots - any "to" before "from" is now a valid overnight session
  const validateTimeSlots = () => {
    // We're no longer validating that "to time" must be after "from time"
    // This function now just ensures both fields are filled
    let hasErrors = false;

    Object.keys(days).forEach(day => {
      if (days[day]) {
        timeSlots[day].forEach(slot => {
          if (!slot.from || !slot.to) {
            setErrorMessage('Please fill in all time slots');
            hasErrors = true;
          }
        });
      }
    });

    return !hasErrors;
  };

  const validateForm = () => {
    // Check if at least one day is selected
    const anyDaySelected = Object.values(days).some(value => value);
    if (!anyDaySelected) {
      setErrorMessage('Please select at least one day');
      return false;
    }

    // Validate time slots
    if (!validateTimeSlots()) {
      return false;
    }

    return true;
  };

  const handleContinue = async () => {
    if (validateForm()) {
      setIsLoading(true);

      try {
        // Format data for API - convert to 24h format in UTC
        const timeSlotsByDay = {};

        Object.keys(days).forEach(day => {
          if (days[day]) {
            const capitalizedDay = day.charAt(0).toUpperCase() + day.slice(1);
            timeSlotsByDay[capitalizedDay] = timeSlots[day].map(slot => {
              // First, parse the local times using dayjs
              const fromTime = dayjs(`2023-01-01 ${slot.from}`, 'YYYY-MM-DD hh:mm A');
              const toTime = dayjs(`2023-01-01 ${slot.to}`, 'YYYY-MM-DD hh:mm A');

              // Check if this is an overnight session (to < from in local time)
              const isOvernightShift = toTime.isBefore(fromTime);

              // Convert each time to UTC
              const startUTC = fromTime.utc().format('HH:mm');

              // For overnight shifts, add a day to the end time before converting to UTC
              let endUTC;
              if (isOvernightShift) {
                const toTimeNextDay = toTime.add(1, 'day');
                endUTC = toTimeNextDay.utc().format('HH:mm');
              } else {
                endUTC = toTime.utc().format('HH:mm');
              }

              return {
                start: startUTC,
                end: endUTC,
              };
            });
          }
        });

        const requestData = {
          session_type: 'one_on_one',
          description: description,
          time_slots: timeSlotsByDay,
        };

        // Make API call
        await axiosInstance.post('/teachers/availability', requestData);

        // Show success message
        setShowSuccess(true);
        toast.success('One-on-one session created successfully!');

        // Call onContinue if provided
        if (onContinue) {
          const selectedDaysWithTimeSlots = {};
          Object.keys(days).forEach(day => {
            if (days[day]) {
              selectedDaysWithTimeSlots[day] = timeSlots[day];
            }
          });
          onContinue(selectedDaysWithTimeSlots, description);
        }

        // Reset form after 2 seconds and close modal
        setTimeout(() => {
          resetForm();
          setShowSuccess(false);
          onClose();
        }, 2000);
      } catch (error) {
        console.error('Error creating availability:', error);

        // Format error message from API correctly
        let errorMsg = 'Failed to create availability. Please try again.';

        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (Array.isArray(detail) && detail.length > 0) {
            // Extract message from the API error object
            errorMsg = detail[0].msg || errorMsg;
          } else if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (typeof detail === 'object') {
            errorMsg = JSON.stringify(detail);
          }
          toast.error(errorMsg);
        }

        setErrorMessage(errorMsg);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const resetForm = () => {
    setDays({
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    });

    setTimeSlots({
      monday: [{ from: '', to: '' }],
      tuesday: [{ from: '', to: '' }],
      wednesday: [{ from: '', to: '' }],
      thursday: [{ from: '', to: '' }],
      friday: [{ from: '', to: '' }],
      saturday: [{ from: '', to: '' }],
      sunday: [{ from: '', to: '' }],
    });

    setDescription('');
    setErrorMessage('');
  };

  // Add a function to render the time picker as a portal
  const renderTimePickerPortal = () => {
    if (!activeTimePicker) return null;

    const { day, index, field } = activeTimePicker;
    const timeValue = stringToTime(timeSlots[day][index][field]);

    // Get the reference key for this time picker
    const refKey = `${day}-${index}-${field}`;
    const inputElement = timePickerRefs.current[refKey];

    return ReactDOM.createPortal(
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <TimePicker
          value={timeValue}
          onChange={newValue => handleTimeChange(day, index, field, newValue)}
          onClose={handleTimePickerClose}
          className="time-picker-portal"
          ampm={true}
          format="hh:mm A"
          minutesStep={15}
          open={true}
          viewRenderers={{
            hours: customTimeViewRenderer,
            minutes: customTimeViewRenderer,
          }}
          views={['hours', 'minutes']}
          view={clockView}
          onViewChange={newView => setClockView(newView)}
          slots={{
            textField: () => null, // Hide the default text field
          }}
          slotProps={{
            popper: {
              style: { zIndex: 2500 },
              placement: 'bottom-start',
              anchorEl: inputElement,
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 5],
                  },
                },
              ],
            },
          }}
        />
      </LocalizationProvider>,
      document.body
    );
  };

  // Modify renderDayTimeSlots to use custom time input wrapper
  const renderDayTimeSlots = day => {
    if (!days[day]) return null;

    return timeSlots[day].map((slot, index) => (
      <Box key={`${day}-${index}`} className="time-slot-container">
        <Box className="time-inputs">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box className="time-input-group-one-on-one">
              <Typography className="time-label">From</Typography>
              <div
                className="time-input-wrapper"
                onClick={() => handleTimePickerOpen(day, index, 'from')}
                ref={el => {
                  // Store the ref for this specific time input field
                  timePickerRefs.current[`${day}-${index}-from`] = el;
                }}
              >
                <div className="time-input-icon">
                  <AccessTimeIcon fontSize="small" />
                </div>
                <input
                  className="time-input-field"
                  value={slot.from || ''}
                  placeholder="Select time"
                  readOnly
                />
              </div>
            </Box>

            <Box className="time-input-group-one-on-one">
              <Typography className="time-label">To</Typography>
              <div
                className="time-input-wrapper"
                onClick={() => handleTimePickerOpen(day, index, 'to')}
                ref={el => {
                  // Store the ref for this specific time input field
                  timePickerRefs.current[`${day}-${index}-to`] = el;
                }}
              >
                <div className="time-input-icon">
                  <AccessTimeIcon fontSize="small" />
                </div>
                <input
                  className="time-input-field"
                  value={slot.to || ''}
                  placeholder="Select time"
                  readOnly
                />
              </div>
            </Box>
          </LocalizationProvider>

          <Box className="time-slot-actions">
            <IconButton className="time-slot-action-button" onClick={() => addTimeSlot(day)}>
              <AddIcon fontSize="small" />
            </IconButton>

            <IconButton
              className="time-slot-action-button"
              onClick={e => openCopyPopover(e, day, index)}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>

            <IconButton
              className="time-slot-action-button"
              onClick={() => removeTimeSlot(day, index)}
              disabled={timeSlots[day].length === 1}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {errorMessage && errorMessage.includes(`${day.charAt(0).toUpperCase() + day.slice(1)}`) && (
          <Typography
            className="error-message"
            sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}
          >
            {errorMessage}
          </Typography>
        )}
      </Box>
    ));
  };

  return (
    <Modal open={open} onClose={onClose} className="one-on-one-modal">
      <Box className="one-on-one-modal-content">
        {showSuccess ? (
          <ScheduleCreationSuccessModel sessionType="One on One" />
        ) : (
          <>
            <Box className="one-on-one-modal-header">
              <Typography className="one-on-one-modal-title">
                Add a New One on One Session
              </Typography>
              <IconButton onClick={onClose} className="close-button">
                <CloseIcon />
              </IconButton>
            </Box>

            <Box className="one-on-one-modal-body">
              <Box className="days-selection">
                {Object.keys(days).map(day => (
                  <Box key={day} className="day-container">
                    <Box className="day-header">
                      <Typography className="day-name-one-on-one">
                        {day.charAt(0).toUpperCase() + day.slice(1)}
                      </Typography>
                      <Switch
                        checked={days[day]}
                        onChange={() => handleDayToggle(day)}
                        color="primary"
                        className="day-switch-one-on-one"
                      />
                    </Box>
                    {renderDayTimeSlots(day)}
                  </Box>
                ))}
              </Box>

              <Box className="description-container">
                <Typography className="description-label">Write a short description</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Enter here..."
                  value={description}
                  onChange={e => {
                    setDescription(e.target.value);
                    setErrorMessage('');
                  }}
                  variant="outlined"
                  className="description-field"
                />
              </Box>
            </Box>

            <Box className="one-on-one-modal-footer">
              <Button variant="outlined" onClick={onClose} className="cancel-button-one-on-one">
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleContinue}
                className="continue-button"
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Continue'}
              </Button>
            </Box>

            <Popover
              open={Boolean(copyAnchorEl)}
              anchorEl={copyAnchorEl}
              onClose={closeCopyPopover}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              className="copy-popover"
            >
              <Box className="copy-popover-content">
                <Typography className="copy-popover-title">Copy Times TO</Typography>
                <Box className="copy-days-list">
                  {Object.keys(copyTargetDays)
                    .filter(day => day !== copySourceDay)
                    .map(day => (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            checked={copyTargetDays[day]}
                            onChange={() => handleCopyTargetChange(day)}
                            color="primary"
                          />
                        }
                        label={day.charAt(0).toUpperCase() + day.slice(1)}
                        className="copy-day-checkbox"
                      />
                    ))}
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={applyCopy}
                  className="apply-copy-button"
                >
                  Apply
                </Button>
              </Box>
            </Popover>

            {/* Add the time picker portal at the end */}
            {renderTimePickerPortal()}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default CreateOneOnOneSessionModel;
